import { useEffect, useState } from "react";
import {
  Box, Paper, Stack, Button, Alert, TextField, InputAdornment,
  Dialog, DialogContent, DialogActions, Divider, Typography, CircularProgress,
} from "@mui/material";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { genericNamesApi, type GenericNameDto, type GenericNameDetails } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { StatusPill } from "@/components/StatusPill";

const QUERY_KEY = "generic-names";

export function GenericNamesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const canCreate = useAuth(s => s.hasPermission("catalog.generic.create"));
  const canUpdate = useAuth(s => s.hasPermission("catalog.generic.edit"));
  const canDelete = useAuth(s => s.hasPermission("catalog.generic.delete"));

  const query = useQuery({
    queryKey: [QUERY_KEY, search, pagination.page, pagination.pageSize],
    queryFn: () => genericNamesApi.list({
      search: search || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "Generic name deleted" },
    mutationFn: (id: string) => genericNamesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });

  const openCreate = () => { setEditingId(null); setFormOpen(true); };
  const openEdit = (row: GenericNameDto) => { setEditingId(row.id); setFormOpen(true); };
  const handleDelete = (row: GenericNameDto) => {
    if (confirm(`Delete generic name "${row.name}"? Blocked if any item references it.`)) {
      deleteMutation.mutate(row.id);
    }
  };

  const columns: GridColDef<GenericNameDto>[] = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
    {
      field: "hasDetails", headerName: "Product information", width: 190, sortable: false,
      renderCell: (p) => p.row.hasDetails
        ? <StatusPill label="Captured" tone="success" />
        : <StatusPill label="Not captured" tone="neutral" />,
    },
    {
      field: "actions", headerName: "", width: 96, sortable: false, filterable: false,
      renderCell: (p) => (
        <RowActions actions={[
          { label: "Edit", icon: <EditIcon fontSize="small" />, hidden: !canUpdate, onClick: () => openEdit(p.row) },
          { label: "Delete", icon: <DeleteIcon fontSize="small" />, color: "error", hidden: !canDelete, onClick: () => handleDelete(p.row) },
        ]} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Catalog"]}
        title="Generic Names"
        subtitle="Molecule master for medicines. Product information (composition, uses, warnings…) is optional — record whatever the pack insert gives you."
        actions={
          canCreate ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New generic name
            </Button>
          ) : undefined
        }
      />
      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by name"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p) => canUpdate && openEdit(p.row)}
          />
        </Box>
      </Paper>

      <GenericNameDialog open={formOpen} onClose={() => setFormOpen(false)} id={editingId} />
    </Box>
  );
}

// =============================== Create / edit dialog ===============================

/** Mirrors generic_names."Name" varchar(500) and the server-side validator. */
const NAME_MAX_LENGTH = 500;

/** The optional monograph fields, in the order they are captured on the form. */
const DETAIL_FIELDS: { key: keyof GenericNameDetails; label: string; rows?: number; maxLength: number }[] = [
  { key: "countryOfOrigin", label: "Country of origin", maxLength: 100 },
  { key: "therapeuticClassification", label: "Therapeutic classification", maxLength: 200 },
  { key: "schedule", label: "Schedule", maxLength: 100 },
  { key: "composition", label: "Composition", rows: 2, maxLength: 2000 },
  { key: "ingredients", label: "Ingredients", rows: 3, maxLength: 2000 },
  { key: "uses", label: "Uses", rows: 3, maxLength: 2000 },
  { key: "directionsForUse", label: "Directions for use", rows: 3, maxLength: 2000 },
  { key: "contraindications", label: "Contraindications", rows: 3, maxLength: 2000 },
  { key: "sideEffects", label: "Side effects", rows: 3, maxLength: 2000 },
  { key: "precautionsAndWarnings", label: "Precautions and warnings", rows: 3, maxLength: 2000 },
  { key: "safetyInformation", label: "Safety information", rows: 3, maxLength: 2000 },
];

const EMPTY_DETAILS: GenericNameDetails = Object.fromEntries(
  DETAIL_FIELDS.map(f => [f.key, ""]),
) as GenericNameDetails;

function GenericNameDialog({ open, onClose, id }: { open: boolean; onClose: () => void; id: string | null }) {
  const qc = useQueryClient();
  const isEdit = !!id;
  const [name, setName] = useState("");
  const [details, setDetails] = useState<GenericNameDetails>(EMPTY_DETAILS);

  // Monograph text isn't carried on list rows, so the row being edited is fetched in full.
  const detailQuery = useQuery({
    queryKey: [QUERY_KEY, "detail", id],
    queryFn: () => genericNamesApi.get(id!),
    enabled: open && isEdit,
  });

  useEffect(() => {
    if (!open) return;
    if (!isEdit) { setName(""); setDetails(EMPTY_DETAILS); return; }
    const d = detailQuery.data;
    if (!d) return;
    setName(d.name);
    setDetails(Object.fromEntries(
      DETAIL_FIELDS.map(f => [f.key, d[f.key] ?? ""]),
    ) as GenericNameDetails);
  }, [open, isEdit, detailQuery.data]);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Generic name updated" : "Generic name created" },
    mutationFn: async () => {
      // Blank boxes are sent as null — "not given" stays absent rather than an empty string.
      const payloadDetails = Object.fromEntries(
        DETAIL_FIELDS.map(f => [f.key, (details[f.key] ?? "").trim() || null]),
      ) as GenericNameDetails;
      const payload = { name: name.trim(), details: payloadDetails };
      if (isEdit && id) { await genericNamesApi.update(id, payload); return id; }
      const r = await genericNamesApi.create(payload);
      return r.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      onClose();
    },
  });

  const tooLong = DETAIL_FIELDS.find(f => (details[f.key] ?? "").length > f.maxLength);
  const err = !name.trim()
    ? "Name required"
    : name.trim().length > NAME_MAX_LENGTH
      ? `Name is limited to ${NAME_MAX_LENGTH} characters`
      : tooLong
        ? `${tooLong.label} is limited to ${tooLong.maxLength} characters`
        : null;
  const loading = isEdit && detailQuery.isLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err) mutation.mutate(); }}>
        <ClosableDialogTitle onClose={onClose}>
          {isEdit ? "Edit generic name" : "New generic name"}
        </ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
          {detailQuery.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(detailQuery.error)}</Alert>}
          {loading ? (
            <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} /></Stack>
          ) : (
            <Stack spacing={2}>
              <TextField label="Name *" size="small" fullWidth autoFocus
                value={name} onChange={(e) => setName(e.target.value)} />
              {err && <Alert severity="info">{err}</Alert>}

              <Divider />
              <Box>
                <Typography variant="subtitle2">Product information</Typography>
                <Typography variant="caption" color="text.secondary">
                  All optional — fill in what the supplier or pack insert provides, leave the rest blank.
                </Typography>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                {DETAIL_FIELDS.filter(f => !f.rows).map(f => (
                  <DetailField key={f.key} field={f} value={details[f.key] ?? ""}
                    onChange={v => setDetails(d => ({ ...d, [f.key]: v }))} />
                ))}
              </Stack>
              {DETAIL_FIELDS.filter(f => f.rows).map(f => (
                <DetailField key={f.key} field={f} value={details[f.key] ?? ""}
                  onChange={v => setDetails(d => ({ ...d, [f.key]: v }))} />
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || loading || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function DetailField({
  field, value, onChange,
}: {
  field: (typeof DETAIL_FIELDS)[number];
  value: string;
  onChange: (v: string) => void;
}) {
  const over = value.length > field.maxLength;
  return (
    <TextField
      label={field.label} size="small" fullWidth
      multiline={!!field.rows} minRows={field.rows} maxRows={field.rows ? field.rows + 4 : undefined}
      value={value} onChange={(e) => onChange(e.target.value)}
      error={over}
      helperText={over ? `Maximum ${field.maxLength} characters (${value.length} used)` : undefined}
    />
  );
}
